import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import { betterFetch, betterFetchFactory } from '@e-socna/better-fetch';
import type { BetterFetchLogger } from '@e-socna/better-fetch';

import { GEOPL_API_PROXY_ENDPOINT } from '../proxy/constants/endpoints.constant';

import { createGeoplFetcher } from './fetcher';
import { GEO_CODING_SEARCH_API_ENDPOINT } from './constants/endpoints.constant';
import type { GeoCodingResult } from './types/geo-coding-result.type';
import type { Address } from './types/address.type';
import type { Parcel } from './types/parcel.type';

export type GeocodingSearchIndex = 'parcel' | 'address';

interface Params<GSI extends GeocodingSearchIndex> {
  readonly q?: string;
  readonly coordinates?: readonly number[];
  readonly index?: GSI;
  readonly limit?: number;
  readonly returntruegeometry?: boolean;
  readonly departmentcode?: string;
  readonly municipalitycode?: string;
  readonly oldmunicipalitycode?: string;
  readonly districtcode?: string;
  readonly section?: string;
  readonly number?: string;
  readonly sheet?: string;
  readonly enabled?: boolean;
}

export type { Params as GeocodingSearchParams };

const DEFAULT_INDEX = 'address';
const DEFAULT_LIMIT = '1';
const DEFAULT_RETURN_TRUE_GEOMETRY = 'false';

export const geocodingSearchFetcher = <GSI extends GeocodingSearchIndex>(
  pParams: Params<GSI>,
  backendUrl: string,
  logger?: BetterFetchLogger,
) => {
  const params = {
    q: pParams.q ?? EMPTY_STRING,
    ...(pParams.coordinates
      ? {
          lon: pParams.coordinates[0]?.toFixed(6),
          lat: pParams.coordinates[1]?.toFixed(6),
        }
      : EMPTY_OBJECT),
    index: pParams.index ?? DEFAULT_INDEX,
    limit: pParams.limit?.toFixed() ?? DEFAULT_LIMIT,
    returntruegeometry: pParams.returntruegeometry
      ? String(pParams.returntruegeometry)
      : DEFAULT_RETURN_TRUE_GEOMETRY,
    ...(pParams.departmentcode
      ? { departmentcode: pParams.departmentcode }
      : EMPTY_OBJECT),
    ...(pParams.municipalitycode
      ? { municipalitycode: pParams.municipalitycode }
      : EMPTY_OBJECT),
    ...(pParams.oldmunicipalitycode
      ? { oldmunicipalitycode: pParams.oldmunicipalitycode }
      : EMPTY_OBJECT),
    ...(pParams.districtcode
      ? { districtcode: pParams.districtcode }
      : EMPTY_OBJECT),
    ...(pParams.section ? { section: pParams.section } : EMPTY_OBJECT),
    ...(pParams.number ? { number: pParams.number } : EMPTY_OBJECT),
    ...(pParams.sheet ? { sheet: pParams.sheet } : EMPTY_OBJECT),
  };

  const queryString = new URLSearchParams(params).toString();

  const url = `${backendUrl}${GEOPL_API_PROXY_ENDPOINT}/${GEO_CODING_SEARCH_API_ENDPOINT}?${queryString}`;

  const fetch = logger ? betterFetchFactory(logger) : betterFetch;
  const geoplFetch = createGeoplFetcher('geocoding', fetch);

  return {
    url,
    fetcher: () =>
      geoplFetch<GeoCodingResult<GSI extends 'address' ? Address : Parcel>>(
        url,
      ),
  };
};
